'use client';
import { useEffect, useRef, useState } from 'react';
import { getOwnRelease } from '@/helper/ownRelease';
import { clearAllClientStorage, decideVersionAction, triggerPageReload } from '@/helper/versionActions';

// =========================================================
// Version polling hook
// =========================================================
//
// Detects when a new version of the app has been deployed while the
// user's browser session is still running the old one. The hook is
// wired up once, near the root of the tree; the returned
// `updateAvailable` flag is consumed by the update banner component.
//
// Design decisions worth noting for future readers:
//
//   - The hook holds no record of what it has seen. It compares the
//     served release against the release baked into this bundle
//     (`getOwnRelease`), so a forced reload that lands back on a stale
//     replica is retried on the next poll instead of being written off
//     as already done. A rolling deploy can reload a tab more than
//     once; it stops when the tab actually runs what the cluster
//     serves.
//
//   - Two outcomes on a version change. By default a change only
//     raises the banner and the operator taps to reload. When the
//     deployment sets `forceUiReload`, the hook wipes client storage
//     and reloads on its own; the decision lives in
//     `decideVersionAction`. Wiping signs the user out.
//
//   - Keep polling after the banner. The banner is not a terminal
//     state: an operator can flip `forceUiReload` on a later deploy to
//     escalate an already-shown banner into a forced reload, so the
//     poll must stay alive to see it.
//
//   - False-positive safety. A failed fetch never flips the flag;
//     only a successful response with a *different* non-empty
//     release does. This matters for mobile MDAs that frequently
//     wander out of wifi coverage — we must not confuse "I can't
//     reach the server right now" with "a new version is live".
//
//   - Cache-busting is belt-and-suspenders. `cache: 'no-store'` +
//     server-side no-store headers are already enough in theory;
//     appending `?t=${Date.now()}` makes caching physically
//     impossible under any intermediary that keys on URL (all of
//     them). Free insurance against future misconfigurations.
//
//   - Multiple triggers. 30s timer covers the idle case; window
//     `online` event covers reconnects; document `visibilitychange`
//     covers tab re-focus after the timer has been throttled in
//     the background; `pageshow` with `event.persisted` covers
//     restoration from the browser's back/forward cache, where
//     in-memory React state is restored wholesale and neither the
//     timer nor visibilitychange reliably fires.
//
//   - Force fires once. An in-memory latch stops overlapping probes
//     (timer + focus + online) from both reaching the reload, and the
//     sessionStorage guard read here bounds a rolling deploy to a
//     single reload per target release.

const DEFAULT_INTERVAL_MS = 30_000;
const FETCH_TIMEOUT_MS = 5_000;

export interface VersionPollResult {
  updateAvailable: boolean;
}

interface RawVersionPayload {
  release: string;
  forceUiReload: boolean;
}

// Runtime extraction of the payload fields from an arbitrary JSON
// value. Uses `in` narrowing (no type assertions, per the repo's
// `no-type-assertion` rule) so a malformed response cannot cause a
// confident-but-wrong read. Returns null when there is no usable
// (non-empty) release — the caller treats that as "nothing to act on".
const extractPayload = (parsed: unknown): RawVersionPayload | null => {
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const release = 'release' in parsed && typeof parsed.release === 'string' ? parsed.release : '';
  if (release === '') {
    return null;
  }
  const forceUiReload = 'forceUiReload' in parsed && parsed.forceUiReload === true;
  return { release, forceUiReload };
};

export function useVersionPoll(endpoint: string, intervalMs: number = DEFAULT_INTERVAL_MS): VersionPollResult {
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
  const isForcingRef = useRef<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    // Attempts one fetch and routes the result through
    // `decideVersionAction`. Raises the banner or force-reloads when the
    // served release differs from this bundle's own, and returns early
    // on any failure — a failed poll is explicitly not evidence of a
    // deploy.
    const probe = async (): Promise<void> => {
      // Once a forced reload is in flight the page is on its way out;
      // further probes would only race the navigation.
      if (cancelled || isForcingRef.current) {
        return;
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort();
      }, FETCH_TIMEOUT_MS);

      try {
        // Cache-buster query param on top of fetch-layer no-store.
        const separator = endpoint.includes('?') ? '&' : '?';
        const bustedUrl = `${endpoint}${separator}t=${Date.now()}`;

        const response = await fetch(bustedUrl, {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          return;
        }

        const parsed: unknown = await response.json();
        // A probe that suspended in `fetch` or `json` can resume after
        // another probe has already started the reload.
        if (cancelled || isForcingRef.current) {
          return;
        }

        const payload = extractPayload(parsed);
        if (payload === null) {
          return;
        }

        const action = decideVersionAction(payload, getOwnRelease());

        switch (action.kind) {
          case 'banner':
            setUpdateAvailable(true);
            break;
          case 'force':
            // Latch first: everything after this point is synchronous,
            // so no other probe can enter the branch behind us.
            isForcingRef.current = true;
            clearAllClientStorage();
            triggerPageReload();
            break;
          case 'none':
          default:
            // Includes the case where a poll matches again after a
            // banner was shown, which un-shows it: the release that
            // prompted the banner is no longer the one being served.
            setUpdateAvailable(false);
            break;
        }
      } catch {
        // Swallowed deliberately. Transient network failures,
        // aborted timeouts, JSON parse errors — none of these are
        // evidence of a new deploy, so we silently retry on the
        // next tick.
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    // Kick off the first probe immediately on mount so the baseline
    // is established as early as possible.
    void probe();

    const timerId = window.setInterval(() => {
      void probe();
    }, intervalMs);

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void probe();
      }
    };
    const handleOnline = (): void => {
      void probe();
    };
    const handlePageShow = (event: PageTransitionEvent): void => {
      // `persisted` is true when the page is being restored from the
      // browser's back/forward cache. In-memory React state comes
      // back wholesale and no other trigger fires reliably.
      if (event.persisted) {
        void probe();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      cancelled = true;
      window.clearInterval(timerId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [endpoint, intervalMs]);

  return { updateAvailable };
}
